import React, { useCallback, useEffect, useRef } from 'react'

import { useDynamicIsland } from './useDynamicIsland'
import { FlashContent } from './FlashContent'
import type { FlashMessageType } from './FlashContent'

export function useFlashMessage() {
  const { pushRows, popRows } = useDynamicIsland()

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dismissRef = useRef<() => void>(() => {})

  dismissRef.current = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    popRows()
  }

  const dismiss = useCallback(() => {
    dismissRef.current()
  }, [])

  const showFlash = useCallback(
    (message: string, type: FlashMessageType = 'info', duration = 4000) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      pushRows([
        [
          {
            id: `flash-${Date.now()}`,
            meta: { flashType: type },
            component: React.createElement(FlashContent, {
              message,
              type,
              onDismiss: dismiss,
            }),
          },
        ],
      ])

      timeoutRef.current = setTimeout(() => {
        dismissRef.current()
      }, duration)
    },
    [pushRows, dismiss]
  )

  // Clean up the auto-dismiss timeout when the component that called
  // useFlashMessage unmounts. Without this, navigating away (e.g. from
  // event creation to event settings) leaves a stale timeout that fires
  // popRows() and removes the new page's DI rows.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  return { showFlash, dismiss }
}
